// Proveedor de routing: Google Routes API (vía backend, principal) con OSRM (respaldo).
// La clave de Google vive SOLO en el backend. OSRM se conserva como respaldo temporal.
// Regla: si el routing falla, devolver error — NUNCA usar distancia en línea
// recta como fallback silencioso para la cotización.
// Rate-limit friendly: debounce en el llamador + caché en memoria de rutas repetidas.

import { authHeaders } from "@/lib/supabase";

const API = process.env.REACT_APP_BACKEND_URL;
const OSRM_BASE = "https://router.project-osrm.org";

const cache = new Map(); // key -> { t, value }
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min

/**
 * get_road_distance(origin, destination)
 * @param {{lat:number,lng:number}} origin
 * @param {{lat:number,lng:number}} destination
 * @returns {Promise<{ok:boolean, distanceKm?:number, durationMin?:number, geometry?:Array, provider?:string, error?:string}>}
 */
export async function get_road_distance(origin, destination) {
  const key = `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}|${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.t < CACHE_TTL_MS) return hit.value;

  // 1) Principal: backend (Google Routes con respaldo OSRM del lado servidor)
  try {
    const res = await fetch(`${API}/api/routes/compute`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ origin, destination }),
      signal: AbortSignal.timeout(15000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.ok && data.distanceKm != null) {
        const result = {
          ok: true,
          distanceKm: data.distanceKm,
          durationMin: data.durationMin,
          geometry: data.geometry || null,
          provider: data.provider,
        };
        cache.set(key, { t: Date.now(), value: result });
        return result;
      }
      if (data && data.ok === false && data.error) {
        return { ok: false, error: data.error };
      }
    }
  } catch (e) {
    // cae al respaldo OSRM directo
  }

  // 2) Respaldo: OSRM público directo (se conserva mientras se validan las pruebas de Google)
  let result;
  try {
    const url = `${OSRM_BASE}/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson&steps=false`;
    const res = await fetch(url, { signal: AbortSignal.timeout(15000) });
    if (!res.ok) {
      result = { ok: false, error: "El servicio de rutas no respondió. Intenta de nuevo." };
    } else {
      const data = await res.json();
      const route = data?.routes?.[0];
      if (data?.code !== "Ok" || !route) {
        result = { ok: false, error: "No se encontró una ruta por carretera entre esos puntos. Ajusta el origen o destino." };
      } else {
        result = {
          ok: true,
          distanceKm: +(route.distance / 1000).toFixed(2),
          durationMin: Math.max(1, Math.round(route.duration / 60)),
          geometry: route.geometry?.coordinates || null, // GeoJSON [lng,lat][] para dibujar la ruta real
          provider: "osrm",
        };
        cache.set(key, { t: Date.now(), value: result });
      }
    }
  } catch (e) {
    result = { ok: false, error: "No se pudo contactar el servicio de rutas. Revisa tu conexión e intenta de nuevo." };
  }
  return result;
}
